import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

// Horários de funcionamento por dia da semana (0 = Domingo, 1 = Segunda, etc.)
const STORE_HOURS = {
  0: { open: '19:00', close: '23:45', isOpen: true },  // Domingo
  1: { open: '19:00', close: '23:45', isOpen: true },  // Segunda
  2: { open: null, close: null, isOpen: false },       // Terça - FECHADO
  3: { open: '19:00', close: '23:45', isOpen: true },  // Quarta
  4: { open: '19:00', close: '23:45', isOpen: true },  // Quinta
  5: { open: '19:00', close: '23:45', isOpen: true },  // Sexta
  6: { open: '19:00', close: '23:45', isOpen: true },  // Sábado
};

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const DAY_NAMES_SHORT = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';
const WEEKDAY_MAP = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const getBrazilNow = () => {
  const now = new Date();
  const weekdayName = new Intl.DateTimeFormat('en-US', {
    timeZone: BRAZIL_TIME_ZONE,
    weekday: 'short',
  }).format(now);

  const timeParts = new Intl.DateTimeFormat('en-US', {
    timeZone: BRAZIL_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const hour = Number(timeParts.find((part) => part.type === 'hour')?.value ?? now.getHours());
  const minute = Number(timeParts.find((part) => part.type === 'minute')?.value ?? now.getMinutes());

  return {
    dayOfWeek: WEEKDAY_MAP[weekdayName] ?? now.getDay(),
    currentTime: hour * 60 + minute,
  };
};

export const DELIVERY_OPTIONS = [
  { id: 'delivery', label: 'Delivery', icon: 'Truck', description: 'Entrega em domicílio' },
  { id: 'pickup', label: 'Retirada', icon: 'Store', description: 'Retirar no balcão' },
  { id: 'dine-in', label: 'Consumo no Local', icon: 'Utensils', description: 'Comer na pizzaria' },
];

export const PAYMENT_OPTIONS = [
  { id: 'cash', label: 'Dinheiro', icon: 'Banknote' },
  { id: 'credit', label: 'Cartão de Crédito', icon: 'CreditCard' },
  { id: 'debit', label: 'Cartão de Débito', icon: 'CreditCard' },
  { id: 'pix', label: 'Pix Manual', icon: 'QrCode' },
];

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [storeStatus, setStoreStatus] = useState({ isOpen: false, message: '' });

  // Verificar status da loja
  const checkStoreStatus = useCallback(() => {
    const { dayOfWeek, currentTime } = getBrazilNow();
    const todayHours = STORE_HOURS[dayOfWeek];

    if (!todayHours.isOpen) {
      const nextOpen = getNextOpenDay(dayOfWeek);
      return {
        isOpen: false,
        message: `Fechado hoje (${DAY_NAMES[dayOfWeek]}). Voltamos ${nextOpen}.`,
        nextOpen,
      };
    }

    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;

    if (currentTime >= openTime && currentTime <= closeTime) {
      return {
        isOpen: true,
        message: `Aberto agora • Fecha as ${todayHours.close} (horario de Brasilia).`,
      };
    }

    if (currentTime < openTime) {
      return {
        isOpen: false,
        message: `Fechado • Abre hoje as ${todayHours.open} (horario de Brasilia).`,
        opensAt: todayHours.open,
      };
    }

    const nextOpen = getNextOpenDay(dayOfWeek);
    return {
      isOpen: false,
      message: `Fechado • Voltamos ${nextOpen} (horario de Brasilia).`,
      nextOpen,
    };
  }, []);

  const getNextOpenDay = (currentDay) => {
    for (let i = 1; i <= 7; i++) {
      const nextDay = (currentDay + i) % 7;
      if (STORE_HOURS[nextDay].isOpen) {
        return `${DAY_NAMES[nextDay]} às ${STORE_HOURS[nextDay].open}`;
      }
    }
    return 'Em breve';
  };

  useEffect(() => {
    const updateStatus = () => {
      setStoreStatus(checkStoreStatus());
    };
    updateStatus();
    const interval = setInterval(updateStatus, 60000); // Atualiza a cada minuto
    return () => clearInterval(interval);
  }, [checkStoreStatus]);

  // Adicionar item ao carrinho
  const addItem = (item) => {
    setItems((prev) => {
      const existingIndex = prev.findIndex((i) => i.id === item.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += 1;
        return updated;
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  // Remover item do carrinho
  const removeItem = (itemId) => {
    setItems((prev) => prev.filter((i) => i.id !== itemId));
  };

  // Atualizar quantidade
  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, quantity } : i))
    );
  };

  // Limpar carrinho
  const clearCart = () => {
    setItems([]);
  };

  // Calcular total
  const getTotal = () => {
    return items.reduce((total, item) => {
      const price = parseFloat(item.price.replace('R$', '').replace(',', '.').trim());
      return total + price * item.quantity;
    }, 0);
  };

  // Quantidade total de itens
  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  // Gerar mensagem WhatsApp
  const generateWhatsAppMessage = () => {
    const deliveryLabel = DELIVERY_OPTIONS.find((d) => d.id === deliveryType)?.label || deliveryType;
    const paymentLabel = PAYMENT_OPTIONS.find((p) => p.id === paymentMethod)?.label || paymentMethod;
    const total = getTotal();

    let message = `🍕 *PEDIDO - PIZZARIA 021*\n\n`;
    message += `📋 *ITENS DO PEDIDO:*\n`;
    message += `─────────────────\n`;

    items.forEach((item) => {
      const price = parseFloat(item.price.replace('R$', '').replace(',', '.').trim());
      const subtotal = price * item.quantity;
      message += `• ${item.quantity}x ${item.name}\n`;
      message += `  ${item.price} cada = R$ ${subtotal.toFixed(2).replace('.', ',')}\n\n`;
    });

    message += `─────────────────\n`;
    message += `💰 *SUBTOTAL:* R$ ${total.toFixed(2).replace('.', ',')}\n\n`;
    message += `🚗 *TIPO DE ENTREGA:* ${deliveryLabel}\n`;
    message += `💳 *PAGAMENTO:* ${paymentLabel}\n\n`;

    if (!storeStatus.isOpen) {
      message += `⚠️ *ATENÇÃO:* Pedido enviado fora do horário de funcionamento. Entraremos em contato assim que abrirmos!\n\n`;
    }

    message += `Aguardo confirmação! 😊`;

    return encodeURIComponent(message);
  };

  const getScheduleInfo = () => {
    return Object.entries(STORE_HOURS).map(([day, hours]) => ({
      day: DAY_NAMES_SHORT[day],
      dayFull: DAY_NAMES[day],
      hours: hours.isOpen ? `${hours.open} às ${hours.close}` : 'Fechado',
      isOpen: hours.isOpen,
    }));
  };

  const value = {
    items,
    isCartOpen,
    setIsCartOpen,
    deliveryType,
    setDeliveryType,
    paymentMethod,
    setPaymentMethod,
    storeStatus,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getTotal,
    getTotalItems,
    generateWhatsAppMessage,
    getScheduleInfo,
    DELIVERY_OPTIONS,
    PAYMENT_OPTIONS,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export default CartContext;
